/**
 * MetaExecutionCredentials - MA-ARCH-151
 * 
 * Self-service configuration for Meta Ads API execution credentials.
 * Enables tenants to move from dry_run → live execution.
 * 
 * Key Features:
 * - Connection status display (Not Configured, Dry Run, Live Ready)
 * - Ad Account ID configuration
 * - Access Token entry (masked display)
 * - Validation with real Meta API call
 * - Clear execution mode visibility
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Rocket,
  Shield,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Settings2,
  Key,
  Wallet,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  TestTube,
  Trash2,
  Eye,
  EyeOff,
  Info,
  ExternalLink,
  RefreshCw,
  Clock,
  User,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

// =============================================================================
// STATUS CONFIGURATIONS
// =============================================================================

const executionModeConfig = {
  live: {
    icon: ShieldCheck,
    label: 'Live Execution Ready',
    bgClass: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    description: 'Your actions will execute on Meta Ads platform',
  },
  dry_run: {
    icon: TestTube,
    label: 'Dry Run Mode',
    bgClass: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    description: 'Actions are validated but not executed live',
  },
};

const validationStatusConfig = {
  valid: {
    icon: CheckCircle2,
    label: 'Validated',
    color: 'text-emerald-400',
  },
  invalid: {
    icon: XCircle,
    label: 'Invalid',
    color: 'text-red-400',
  },
  pending: {
    icon: Clock,
    label: 'Not Validated',
    color: 'text-amber-400',
  },
  not_configured: {
    icon: ShieldX,
    label: 'Not Configured',
    color: 'text-slate-400',
  },
  env_fallback: {
    icon: Settings2,
    label: 'Using Environment',
    color: 'text-blue-400',
  },
};

// =============================================================================
// CONNECTION STATUS CARD
// =============================================================================

function ConnectionStatusCard({ config, onValidate, validating }) {
  const isConfigured = config?.configured;
  const mode = config?.execution_mode || 'dry_run';
  const modeConfig = executionModeConfig[mode];
  const ModeIcon = modeConfig?.icon || TestTube;
  
  const validationStatus = config?.validation_status || 'not_configured';
  const valConfig = validationStatusConfig[validationStatus] || validationStatusConfig.not_configured;
  const ValIcon = valConfig.icon;
  
  return (
    <Card className="glass border-white/10" data-testid="meta-connection-status-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              "p-2.5 rounded-lg",
              isConfigured ? "bg-emerald-500/20" : "bg-slate-500/20"
            )}>
              <Rocket className={cn(
                "h-5 w-5",
                isConfigured ? "text-emerald-400" : "text-slate-400"
              )} />
            </div>
            <div>
              <CardTitle className="text-base font-medium text-slate-100">
                Meta Execution Status
              </CardTitle>
              <CardDescription className="text-slate-400 text-sm">
                {isConfigured 
                  ? 'Connected and ready for live execution'
                  : 'Configure credentials to enable live execution'}
              </CardDescription>
            </div>
          </div>
          <Badge className={cn("text-xs border", modeConfig?.bgClass)}>
            <ModeIcon className="h-3 w-3 mr-1" />
            {modeConfig?.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Status Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatusItem
              label="Config Source"
              value={config?.config_source === 'tenant' ? 'Tenant' : 
                     config?.config_source === 'env' ? 'Environment' : 'None'}
              icon={Settings2}
            />
            <StatusItem
              label="Ad Account"
              value={config?.has_access_token && config?.ad_account_id_masked ? config.ad_account_id_masked : 'Not Set'}
              icon={Wallet}
            />
            <StatusItem
              label="Access Token"
              value={config?.has_access_token ? 'Configured' : 'Not Set'}
              icon={Key}
              highlight={config?.has_access_token}
            />
            <StatusItem
              label="Validation"
              value={valConfig.label}
              icon={ValIcon}
              className={valConfig.color}
            />
          </div>
          
          {/* Validation Section */}
          {isConfigured && (
            <div className="pt-3 border-t border-white/10">
              <div className="flex items-center justify-between">
                <div>
                  {config?.last_validated_at && (
                    <p className="text-xs text-slate-400">
                      Last validated: {formatDistanceToNow(new Date(config.last_validated_at), { addSuffix: true })}
                    </p>
                  )}
                  {config?.last_validation_error && validationStatus === 'invalid' && (
                    <p className="text-xs text-red-400 mt-1">
                      {config.last_validation_error}
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                  onClick={onValidate}
                  disabled={validating}
                  data-testid="validate-meta-connection-btn"
                >
                  {validating ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <RefreshCw className="h-3 w-3 mr-1" />
                  )}
                  Validate Connection
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StatusItem({ label, value, icon: Icon, highlight = false, className = '' }) {
  return (
    <div className="p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.05]">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={cn("h-3 w-3 text-slate-400", className)} />
        <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
      </div>
      <p className={cn(
        "text-sm font-medium truncate",
        highlight ? "text-emerald-400" : "text-slate-200",
        className
      )}>
        {value}
      </p>
    </div>
  );
}

// =============================================================================
// CONFIGURATION FORM
// =============================================================================

function ConfigurationForm({ config, onSave, onDelete, saving }) {
  const [adAccountId, setAdAccountId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [pageId, setPageId] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Initialize form with existing values
  useEffect(() => {
    if (config) {
      setAdAccountId(config.ad_account_id || '');
      setPageId(config.page_id || '');
      // Don't show existing token, just indicate it exists
    }
  }, [config]);
  
  const handleSave = async () => {
    if (!adAccountId.trim()) {
      toast.error('Ad Account ID is required');
      return;
    }
    
    // Only require token if not already configured
    if (!config?.has_access_token && !accessToken.trim()) {
      toast.error('Access Token is required for initial setup');
      return;
    }
    
    await onSave({
      ad_account_id: adAccountId.trim(),
      access_token: accessToken.trim() || undefined, // Only send if provided
      page_id: pageId.trim() || undefined,
    });
    
    // Clear token field after save (security)
    setAccessToken('');
  };
  
  const hasExistingConfig = config?.config_source === 'tenant' && config?.configured;
  
  return (
    <Card className="glass border-white/10" data-testid="meta-config-form-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-indigo-500/20">
              <Settings2 className="h-5 w-5 text-indigo-400" />
            </div>
            <div>
              <CardTitle className="text-base font-medium text-slate-100">
                Execution Credentials
              </CardTitle>
              <CardDescription className="text-slate-400 text-sm">
                Configure your Meta Ads API access for live execution
              </CardDescription>
            </div>
          </div>
          {hasExistingConfig && (
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  data-testid="delete-meta-config-btn"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-white/10">
                <DialogHeader>
                  <DialogTitle className="text-slate-100">Remove Configuration</DialogTitle>
                  <DialogDescription className="text-slate-400">
                    This will delete your Meta execution credentials. The system will revert to dry-run mode.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="mt-4">
                  <Button
                    variant="ghost"
                    onClick={() => setShowDeleteDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      onDelete();
                      setShowDeleteDialog(false);
                    }}
                  >
                    Remove Configuration
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Ad Account ID */}
        <div className="space-y-2">
          <Label htmlFor="ad_account_id" className="text-slate-200 text-sm">
            Ad Account ID <span className="text-red-400">*</span>
          </Label>
          <Input
            id="ad_account_id"
            placeholder="act_123456789012345 or 123456789012345"
            value={adAccountId}
            onChange={(e) => setAdAccountId(e.target.value)}
            className="bg-white/5 border-white/10 font-mono"
            data-testid="meta-ad-account-input"
          />
          <p className="text-xs text-slate-400">
            Find in{' '}
            <a 
              href="https://business.facebook.com/settings/ad-accounts" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-400 hover:underline inline-flex items-center gap-0.5"
            >
              Meta Business Settings → Ad Accounts
              <ExternalLink className="h-3 w-3" />
            </a>
          </p>
        </div>
        
        {/* Access Token */}
        <div className="space-y-2">
          <Label htmlFor="access_token" className="text-slate-200 text-sm flex items-center gap-2">
            Access Token
            {config?.has_access_token ? (
              <Badge className="text-[10px] bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                Configured
              </Badge>
            ) : (
              <span className="text-red-400">*</span>
            )}
          </Label>
          <div className="relative">
            <Input
              id="access_token"
              type={showToken ? 'text' : 'password'}
              placeholder={config?.has_access_token ? '••••••••••••••••••••••' : 'EAAxxxxxxxxxxxxxxxx...'}
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              className="bg-white/5 border-white/10 font-mono text-xs pr-10"
              data-testid="meta-access-token-input"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
              onClick={() => setShowToken(!showToken)}
            >
              {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-xs text-slate-400">
            {config?.has_access_token 
              ? 'Leave blank to keep existing token, or enter new token to update'
              : 'System User token with ads_read and ads_management permissions'}
          </p>
        </div>
        
        {/* Page ID (Optional) */}
        <div className="space-y-2">
          <Label htmlFor="page_id" className="text-slate-200 text-sm">
            Facebook Page ID <span className="text-slate-500">(optional)</span>
          </Label>
          <Input
            id="page_id"
            placeholder="123456789012345"
            value={pageId}
            onChange={(e) => setPageId(e.target.value)}
            className="bg-white/5 border-white/10 font-mono"
            data-testid="meta-page-id-input"
          />
          <p className="text-xs text-slate-400">
            Required for creating ad creatives. Find in your Facebook Page settings.
          </p>
        </div>
        
        {/* Save Button */}
        <div className="pt-2">
          <Button
            className="w-full bg-indigo-600 hover:bg-indigo-700"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-meta-config-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Shield className="h-4 w-4 mr-2" />
                {hasExistingConfig ? 'Update Configuration' : 'Save Configuration'}
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// VALIDATION RESULT DISPLAY
// =============================================================================

function ValidationResultDisplay({ result, onClose }) {
  if (!result) return null;
  
  const isValid = result.valid;
  
  return (
    <Dialog open={!!result} onOpenChange={() => onClose()}>
      <DialogContent className="bg-slate-900 border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className={cn(
            "flex items-center gap-2",
            isValid ? "text-emerald-400" : "text-red-400"
          )}>
            {isValid ? (
              <>
                <CheckCircle2 className="h-5 w-5" />
                Validation Successful
              </>
            ) : (
              <>
                <XCircle className="h-5 w-5" />
                Validation Failed
              </>
            )}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {isValid 
              ? 'Your Meta configuration is valid and ready for live execution.'
              : 'Please fix the issues below to enable live execution.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Execution Mode */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
            <span className="text-sm text-slate-300">Execution Mode</span>
            <Badge className={cn(
              "text-xs border",
              isValid 
                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                : "bg-amber-500/20 text-amber-400 border-amber-500/30"
            )}>
              {result.execution_mode === 'live' ? 'Live Ready' : 'Dry Run'}
            </Badge>
          </div>
          
          {/* Ad Account Info */}
          {result.ad_account_info && (
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <p className="text-xs text-emerald-400 uppercase tracking-wider mb-2">Ad Account Verified</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-slate-400">Name:</span>
                  <span className="text-slate-200 ml-2">{result.ad_account_info.name}</span>
                </div>
                <div>
                  <span className="text-slate-400">Currency:</span>
                  <span className="text-slate-200 ml-2">{result.ad_account_info.currency}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Errors */}
          {result.errors?.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-red-400 uppercase tracking-wider">Errors</p>
              {result.errors.map((error, idx) => (
                <div 
                  key={idx}
                  className="flex items-start gap-2 p-2 rounded bg-red-500/10 border border-red-500/20 text-sm text-red-300"
                >
                  <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {error}
                </div>
              ))}
            </div>
          )}
          
          {/* Warnings */}
          {result.warnings?.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-amber-400 uppercase tracking-wider">Warnings</p>
              {result.warnings.map((warning, idx) => (
                <div 
                  key={idx}
                  className="flex items-start gap-2 p-2 rounded bg-amber-500/10 border border-amber-500/20 text-sm text-amber-300"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {warning}
                </div>
              ))}
            </div>
          )}
        </div>
        
        <DialogFooter className="mt-4">
          <Button onClick={onClose} className="w-full">
            {isValid ? 'Done' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function MetaExecutionCredentials() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  
  // Load configuration
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/integrations/meta/config');
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to load Meta config:', error);
      toast.error('Failed to load Meta configuration');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);
  
  // Save configuration
  const handleSave = async (data) => {
    setSaving(true);
    try {
      const response = await api.post('/integrations/meta/config', data);
      
      toast.success('Configuration saved', {
        description: response.data.configured 
          ? 'Ready for live execution. Run validation to verify.'
          : 'Saved. Complete all required fields for live execution.',
      });
      
      await loadConfig();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Please check your inputs and try again.';
      toast.error('Failed to save configuration', {
        description: detail,
      });
    } finally {
      setSaving(false);
    }
  };
  
  // Delete configuration
  const handleDelete = async () => {
    try {
      await api.delete('/integrations/meta/config');
      toast.success('Configuration removed', {
        description: 'System will use dry-run mode for action execution.',
      });
      await loadConfig();
    } catch (error) {
      toast.error('Failed to remove configuration');
    }
  };
  
  // Validate configuration
  const handleValidate = async () => {
    setValidating(true);
    try {
      const response = await api.post('/integrations/meta/validate');
      setValidationResult(response.data);
      
      if (response.data.valid) {
        toast.success('Validation successful', {
          description: 'Your Meta configuration is ready for live execution.',
        });
      } else {
        toast.error('Validation failed', {
          description: 'See details in the dialog.',
        });
      }
      
      // Reload config to get updated validation status
      await loadConfig();
    } catch (error) {
      toast.error('Validation failed', {
        description: error.response?.data?.detail || 'Please try again.',
      });
    } finally {
      setValidating(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="meta-execution-credentials">
      {/* Info Banner */}
      <Alert className="bg-indigo-500/10 border-indigo-500/20">
        <Rocket className="h-4 w-4 text-indigo-400" />
        <AlertTitle className="text-indigo-300">Meta Execution Configuration</AlertTitle>
        <AlertDescription className="text-indigo-300/80">
          Configure your Meta Ads API credentials to enable live action execution. 
          Without configuration, actions will run in dry-run mode for validation only.
        </AlertDescription>
      </Alert>
      
      {/* Connection Status */}
      <ConnectionStatusCard 
        config={config}
        onValidate={handleValidate}
        validating={validating}
      />
      
      {/* Configuration Form */}
      <ConfigurationForm
        config={config}
        onSave={handleSave}
        onDelete={handleDelete}
        saving={saving}
      />
      
      {/* Validation Result Dialog */}
      <ValidationResultDisplay 
        result={validationResult}
        onClose={() => setValidationResult(null)}
      />
      
      {/* Help Text */}
      <Alert className="bg-white/[0.02] border-white/10">
        <Info className="h-4 w-4 text-slate-400" />
        <AlertTitle className="text-slate-300">How execution works</AlertTitle>
        <AlertDescription className="text-slate-400 text-sm">
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Actions are generated by the AI based on performance data</li>
            <li>You approve actions in the Action Queue before execution</li>
            <li>In <strong>dry-run mode</strong>: Actions are validated but not sent to Meta</li>
            <li>In <strong>live mode</strong>: Approved actions execute directly on your Meta Ads account</li>
            <li>All executions are logged with full audit trail</li>
          </ul>
        </AlertDescription>
      </Alert>
    </div>
  );
}

export default MetaExecutionCredentials;
